'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Connection {
  from: string;
  to: string;
  label: string;
}

interface EcosystemDiagramProps {
  connections: Connection[];
}

export default function EcosystemDiagram({ connections }: EcosystemDiagramProps) {
  const [activeConnection, setActiveConnection] = useState<string | null>(null);

  const products = [
    { id: 'knowmax', name: 'KnowMax', icon: 'BookOpenIcon', color: '#1B365D', position: 'top-1/4 left-1/4' },
    { id: 'phygitalmax', name: 'PhygitalMax', icon: 'DevicePhoneMobileIcon', color: '#4A90A4', position: 'top-1/4 right-1/4' },
    { id: 'leapmax', name: 'LeapMax', icon: 'ChartBarIcon', color: '#00D4FF', position: 'bottom-1/4 left-1/4' },
    { id: 'devicemax', name: 'DeviceMax', icon: 'ComputerDesktopIcon', color: '#2E8B57', position: 'bottom-1/4 right-1/4' },
  ];

  return (
    <div className="bg-card rounded-lg shadow-md p-8 border border-border">
      <h3 className="font-display font-bold text-2xl text-primary mb-6 text-center">
        Product Ecosystem Integration
      </h3>

      <div className="relative h-96 bg-surface rounded-lg p-8">
        {/* Central Hub */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
          <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-brand-teal flex items-center justify-center shadow-xl">
            <Icon name="CircleStackIcon" size={40} variant="solid" className="text-white" />
          </div>
          <p className="text-center font-display font-semibold text-sm text-foreground mt-2">
            Unified Data Layer
          </p>
        </div>

        {/* Product Nodes */}
        {products.map((product) => (
          <div
            key={product.id}
            className={`absolute ${product.position} transform -translate-x-1/2 -translate-y-1/2`}
          >
            <div
              className="w-20 h-20 rounded-lg flex items-center justify-center shadow-lg cursor-pointer hover:scale-110 transition-transform duration-300"
              style={{ backgroundColor: product.color }}
              onMouseEnter={() => setActiveConnection(product.id)}
              onMouseLeave={() => setActiveConnection(null)}
            >
              <Icon name={product.icon as any} size={32} variant="solid" className="text-white" />
            </div>
            <p className="text-center font-body font-medium text-xs text-foreground mt-2 w-24">
              {product.name}
            </p>
          </div>
        ))}

        {/* Connection Lines (SVG) */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }}>
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="10"
              refX="9"
              refY="3"
              orient="auto"
            >
              <polygon points="0 0, 10 3, 0 6" fill="#4A90A4" />
            </marker>
          </defs>
          {/* Example connection lines - simplified for demonstration */}
          <line x1="50%" y1="50%" x2="25%" y2="25%" stroke="#4A90A4" strokeWidth="2" strokeDasharray="5,5" markerEnd="url(#arrowhead)" />
          <line x1="50%" y1="50%" x2="75%" y2="25%" stroke="#4A90A4" strokeWidth="2" strokeDasharray="5,5" markerEnd="url(#arrowhead)" />
          <line x1="50%" y1="50%" x2="25%" y2="75%" stroke="#4A90A4" strokeWidth="2" strokeDasharray="5,5" markerEnd="url(#arrowhead)" />
          <line x1="50%" y1="50%" x2="75%" y2="75%" stroke="#4A90A4" strokeWidth="2" strokeDasharray="5,5" markerEnd="url(#arrowhead)" />
        </svg>
      </div>

      {/* Connection Details */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {connections.map((connection, index) => (
          <div
            key={index}
            className={`p-4 rounded-lg border transition-all duration-300 ${
              activeConnection === connection.from || activeConnection === connection.to
                ? 'bg-accent bg-opacity-10 border-accent' :'bg-surface border-border'
            }`}
          >
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="ArrowRightIcon" size={16} variant="solid" className="text-accent" />
              <span className="font-body font-semibold text-sm text-foreground">
                {connection.from} → {connection.to}
              </span>
            </div>
            <p className="font-body text-xs text-muted-foreground">{connection.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}